import type { Weekday } from '../types.ts'

const MS_PER_DAY = 86_400_000

/** Truncates a date to UTC midnight. */
export function startOfUTCDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
}

/** Returns the Monday of the ISO week containing `date`, normalized to UTC midnight. */
export function startOfISOWeek(date: Date): Date {
  const d = startOfUTCDay(date)
  const day = d.getUTCDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setUTCDate(d.getUTCDate() + diff)
  return d
}

export function addDays(date: Date, n: number): Date {
  return new Date(date.getTime() + n * MS_PER_DAY)
}

/** Returns 7 dates Mon..Sun for the ISO week containing `date`. */
export function isoWeekDates(date: Date): Date[] {
  const start = startOfISOWeek(date)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

/** ISO date YYYY-MM-DD in UTC. */
export function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function fromISODate(s: string): Date {
  // s is YYYY-MM-DD; parse as UTC midnight
  return new Date(`${s}T00:00:00.000Z`)
}

/** JS native: 0=Sun..6=Sat. Matches `ScheduleDay.weekday`. */
export function weekdayFromDate(date: Date): Weekday {
  return date.getUTCDay() as Weekday
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  )
}

/** Format a week range, e.g. "May 4 — 10, 2026" or "Apr 28 — May 4, 2026". */
export function formatWeekRange(monday: Date, locale = 'en-US'): string {
  const sunday = addDays(monday, 6)
  const sameMonth = monday.getUTCMonth() === sunday.getUTCMonth()
  const sameYear = monday.getUTCFullYear() === sunday.getUTCFullYear()
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
  const year = sunday.getUTCFullYear()
  if (sameMonth) {
    return `${monthFormatter.format(monday)} ${monday.getUTCDate()} — ${sunday.getUTCDate()}, ${year}`
  }
  if (sameYear) {
    return `${monthFormatter.format(monday)} ${monday.getUTCDate()} — ${monthFormatter.format(sunday)} ${sunday.getUTCDate()}, ${year}`
  }
  return `${monthFormatter.format(monday)} ${monday.getUTCDate()}, ${monday.getUTCFullYear()} — ${monthFormatter.format(sunday)} ${sunday.getUTCDate()}, ${sunday.getUTCFullYear()}`
}

export function formatMonthTitle(date: Date, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

export function formatWeekdayShort(date: Date, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date)
}

/** Returns 6×7 = 42 calendar dates that include the entire month of `date`,
 *  starting on the Monday of the first week and ending on the Sunday of the last.
 *  Used by MiniCalendar. */
export function monthGrid(date: Date): Date[] {
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
  const start = startOfISOWeek(first)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}
